import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import WithListConfig from '../../hocs/WithListConfig';
import fetcher from '../../services/fetcher';
import { toastError, toastSuccess } from '../../utils/ToastConfig';
import ContentMenuActions from '../contentMenuActions';
import { searchParams, stringToStyleObj } from '../../services/commonServices';
import authenticatedFetcher from '../../services/fetcher';
import cx from 'classnames';
import parse from 'html-react-parser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SafeHTML from '../SafeHTML';


// Componente para renderizar select editable
const SelectField = ({ itemToShow, rowData, mainKey, handleChangeInput }) => {
	const [loading, setLoading] = useState(true);
	const [selectData, setSelectData] = useState([]);

	useEffect(() => {
		const urlParams = searchParams(itemToShow.options, rowData);
		authenticatedFetcher({
			path: itemToShow.options.endpoint,
			urlParams
		}).then((res) => {
			setLoading(false);
			setSelectData(res);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (loading) return '...';

	return (
		<select
			className="form-control form-control-sm mt-1 mr-1"
			value={rowData[itemToShow.field]}
			onChange={handleChangeInput(itemToShow, mainKey)}>
			<option value={null}>Seleccione una opción</option>
			{Array.isArray(selectData) &&
				selectData.map((item, i) => (
					<option
						key={`Select: ${itemToShow.field} - option: ${i}`}
						value={item[itemToShow.options.key]}>
						{item[itemToShow.options.print]}
					</option>
				))}
		</select>
	);
};

// Componente para renderizar input integer editable
const IntegerField = ({ itemToShow, rowData, mainKey, handleChangeInput }) => {
	const [hasChange, setHasChange] = useState(false);

	const handleHasChange = () => setHasChange(true);

	return (
		<input
			className="form-control form-control-sm BasicInput-typeNumber"
			defaultValue={rowData[itemToShow.field]}
			type="number"
			onChange={handleHasChange}
			onBlur={hasChange ? handleChangeInput(itemToShow, mainKey) : undefined}
		/>
	);
};

const PlusList = ({ rowsConfig = {}, columns = [], config = {}, ...otherProps }) => {
	const { params } = otherProps;
	const {
		actions: { read: readConfig, delete: deleteConfig },
		fields = [],
		rows: rowInfo = {},
		primary_key_field: primaryKey
	} = config;
	const [t] = useTranslation('app');
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const history = useHistory();

	// Usar la configuración de checkbox del config actual (rowInfo), no del padre (rowsConfig)
	const initColumn = rowInfo.checkbox ? 1 : 0;
	const availableColumns = columns.length;
	const fieldsLength = fields ? fields.length : 0;

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchDataFromEdit = (pathTo) => {
		history.push(pathTo);
	};

	const handleDoubleClick = (path) => () => fetchDataFromEdit(path);

	const handleTrashClick = (itemId) => () => {
		if (!deleteConfig) return;
		return fetcher({ path: `${deleteConfig.uri}${itemId}`, method: deleteConfig.method }).then((res) => {
			if ('error' in res && res.error !== 'no-content') {
				toastError(res.error);
				return false;
			}
			fetchData();
			toastSuccess(t('save'));
			return true;
		});
	};

	const handleLupaClicked = (pathTo) => () => {
		fetchDataFromEdit(pathTo);
		localStorage.setItem('isLupaClicked', true);
	};

	const fetchData = () => {
		if (!readConfig) return;
		fetcher({ path: readConfig.uri, urlParams: params })
			.then((res) => {
				return setData(res);
			})
			.then(() => setLoading(false));
	};

	const handleColumnCheck =
		({ endpoint, method, field }, pk) =>
		async ({ target: { checked } }) => {
			const dataFetched = await fetcher({
				path: `${endpoint}${pk}/`,
				method,
				body: { [field]: checked ? 1 : 0 }
			});
			if ('error' in dataFetched) return toastError(dataFetched.error);
			toastSuccess(t('save'));
			fetchData();
		};

	const handleChangeInput =
		({ endpoint, method, field }, pk) =>
		async ({ target: { value } }) => {
			const dataFetched = await fetcher({
				path: `${endpoint}${pk}/`,
				method,
				body: { [field]: value }
			});
			if ('error' in dataFetched) return toastError(dataFetched.error);
			toastSuccess(t('save'));
			fetchData();
		};

	const renderColumn = (rowData, itemToShow, mainKey) => {
		const classes = rowData[`${itemToShow.field}__class`] ? rowData[`${itemToShow.field}__class`] : '';
		const spanStyle = rowData[`${itemToShow.field}__style`] ? stringToStyleObj(rowData[`${itemToShow.field}__style`]) : {};

		// Renderizar checkbox editable
		if (itemToShow.type === 'check' && !rowData.block_form && itemToShow.endpoint && itemToShow.method) {
			return (
				<input
					type="checkbox"
					defaultChecked={rowData[itemToShow.field]}
					onChange={handleColumnCheck(itemToShow, mainKey)}
				/>
			);
		}

		// Renderizar boolean editable con switch on/off
		if (itemToShow.type === 'boolean' && itemToShow.endpoint && itemToShow.method && !rowData.block_form) {
			return (
				<div 
					onClick={() => handleColumnCheck(itemToShow, mainKey)({ target: { checked: !rowData[itemToShow.field] } })}
					style={{ 
						cursor: 'pointer', 
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: '50px',
						height: '26px',
						borderRadius: '13px',
						backgroundColor: rowData[itemToShow.field] ? '#28a745' : '#ffb3b3',
						position: 'relative',
						transition: 'background-color 0.3s'
					}}
				>
					<div style={{
						position: 'absolute',
						left: rowData[itemToShow.field] ? '26px' : '2px',
						width: '22px',
						height: '22px',
						borderRadius: '50%',
						backgroundColor: '#fff',
						transition: 'left 0.3s',
						boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
					}} />
				</div>
			);
		}

		// Renderizar select editable
		if (itemToShow.type === 'select' && !rowData.block_form && itemToShow.endpoint && itemToShow.method) {
			return <SelectField itemToShow={itemToShow} rowData={rowData} mainKey={mainKey} handleChangeInput={handleChangeInput} />;
		}

		// Renderizar input numérico editable
		if (itemToShow.type === 'integer' && !rowData.block_form && itemToShow.endpoint && itemToShow.method) {
			return <IntegerField itemToShow={itemToShow} rowData={rowData} mainKey={mainKey} handleChangeInput={handleChangeInput} />;
		}

		// Renderizar boolean
		if (typeof rowData[itemToShow.field] === 'boolean') {
			return (
				<SafeHTML
					style={spanStyle}
					className={cx('status-from-buttons-in-table-item-list', classes)}
					html={rowData[itemToShow.field] === true ? 'Sí' : 'No'}
				/>
			);
		}

		// Renderizar link
		if (
			'link' in itemToShow ||
			'blank' in itemToShow ||
			'title_link_fixed' in itemToShow ||
			'title_link' in itemToShow
		) {
			let link_class = itemToShow.link_class || '';
			let label = rowData[itemToShow.title_link] || '';
			if (!label && itemToShow.title_link_fixed) label = itemToShow.title_link_fixed;
			if (!label && !('title_link_fixed' in itemToShow)) label = rowData[itemToShow.field];
			const aProps = itemToShow.blank ? { target: '_blank', rel: 'noopener noreferrer' } : {};
			return (
				<span
					style={spanStyle}
					className={cx('status-from-buttons-in-table-item-list', classes)}>
					<a href={rowData[itemToShow.field]} {...aProps} className={link_class}>
						{label}
					</a>
				</span>
			);
		}

		// Renderizar contenido por defecto
		return (
			<SafeHTML
				style={spanStyle}
				className={cx('status-from-buttons-in-table-item-list', classes)}
				html={rowData[itemToShow.field]}
			/>
		);
	};

	const renderTds = (rowData) => {
		const mainKey = rowData[primaryKey];
		const detailPath = rowData.custom_page ? `/${rowData.custom_page}/${mainKey}` : `${config.uri}${mainKey}`;
		const isEditable = 'no_edit' in rowData ? !rowData.no_edit : true;
		const isRemovable ='no_delete' in rowData ? !rowData.no_delete : true;

		// Estilos para crear borde solo en el perímetro de la fila
		const firstCellStyle = {
			borderTop: '1px solid #e0e0e0',
			borderBottom: '1px solid #e0e0e0',
			borderLeft: '1px solid #e0e0e0'
		};

		const middleCellStyle = {
			borderTop: '1px solid #e0e0e0',
			borderBottom: '1px solid #e0e0e0'
		};

		const lastCellStyle = {
			borderTop: '1px solid #e0e0e0',
			borderBottom: '1px solid #e0e0e0',
			borderRight: '1px solid #e0e0e0'
		};

		const renderDescription = (itemToShow) =>
			`${itemToShow.field}__description` in rowData ? (
				<div className="d-block">{parse(rowData[`${itemToShow.field}__description`])}</div>
			) : null;

		const totalCells = fields.length + (rowInfo.actions ? 1 : 0);
		const hasActions = rowInfo.actions;
		const hasCheckbox = initColumn > 0;

		return fields && Array.isArray(fields) ? (
			<>
				{fields.map((itemToShow, i) => {
					const isFirst = i === 0 && !hasCheckbox;
					const isLast = i === fields.length - 1 && !hasActions;
					const cellStyle = isFirst ? firstCellStyle : isLast ? lastCellStyle : middleCellStyle;
					
					return (
						<td key={`td-pluslist-${i}-${mainKey}`} onDoubleClick={handleDoubleClick(detailPath)} style={cellStyle}>
							{renderColumn(rowData, itemToShow, mainKey)}
							{renderDescription(itemToShow)}
						</td>
					);
				})}
				{availableColumns - fieldsLength > 0 && <td colSpan={availableColumns - fieldsLength} style={middleCellStyle}></td>}
				{hasActions ? (
					<td className="text-center col-outer-tools button-menu-content" style={lastCellStyle}>
						<div className="menu-actions-container">
							{isEditable && isRemovable && (
								<ContentMenuActions
									add
									trash
									lapiz
									edit
									editItem
									handleLupaClick={handleLupaClicked(detailPath)}
									detailPath={detailPath}
									handleTrashClick={handleTrashClick(mainKey)}
								/>
							)}
							{isEditable && !isRemovable && (
								<ContentMenuActions
									add
									lapiz
									edit
									editItem
									handleLupaClick={handleLupaClicked(detailPath)}
									detailPath={detailPath}
								/>
							)}
							{!isEditable && isRemovable && (
								<ContentMenuActions
									add
									trash
									lapiz
									handleLupaClick={handleLupaClicked(detailPath)}
									detailPath={detailPath}
									handleTrashClick={handleTrashClick(mainKey)}
								/>
							)}
						</div>
					</td>
				) : null}
			</>
		) : null;
	};

	const plusRowStyle = {
		backgroundColor: '#f8fbff',
		cursor: 'pointer',
		transition: 'background-color 0.2s ease'
	};

	const checkboxCellStyle = {
		borderTop: '1px solid #e0e0e0',
		borderBottom: '1px solid #e0e0e0',
		borderLeft: '1px solid #e0e0e0'
	};

	const [hoveredRow, setHoveredRow] = useState(null);

	const getRowStyle = (index) => ({
		...plusRowStyle,
		backgroundColor: hoveredRow === index ? '#f0f7ff' : '#f8fbff'
	});

	return !loading && Array.isArray(data) && data.length ? (
		data.map((row, index) => (
			<tr 
				key={`pluslist-row-${index}`} 
				style={getRowStyle(index)}
				onMouseEnter={() => setHoveredRow(index)}
				onMouseLeave={() => setHoveredRow(null)}
			>
				{initColumn > 0 && <td colSpan={initColumn} style={checkboxCellStyle} />}
				{renderTds(row)}
			</tr>
		))
	) : (
		<tr>
			<td colSpan={availableColumns + initColumn + 1} className="text-center">
				{loading ? 'loading...' : 'Sin resultados.'}
			</td>
		</tr>
	);
};

export default WithListConfig(PlusList, { loadingElement: <tr>...</tr> });
